export const CLIMATE_DAYS_PER_YEAR = 365;
export const CENTRAL_EUROPE_LATITUDE_DEGREES = 50;
const TAU = Math.PI * 2;
const radians = Math.PI / 180;
const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
const unit = (value) => clamp(value, 0, 1);
const smooth = (value) => { const t = unit(value); return t * t * (3 - 2 * t); };
/** NOAA fractional-year declination and solar-hour geometry. The game clock is
 * local solar time, so longitude, equation-of-time and DST corrections do not apply.
 * https://gml.noaa.gov/grad/solcalc/solareqns.PDF */
export function sampleSolar(dayOfYear, worldTimeMinutes) {
    const time = ((worldTimeMinutes % 1440) + 1440) % 1440;
    const gamma = TAU / CLIMATE_DAYS_PER_YEAR * (dayOfYear - 1 + time / 1440 - 0.5);
    const declination = 0.006918 - 0.399912 * Math.cos(gamma) + 0.070257 * Math.sin(gamma)
        - 0.006758 * Math.cos(2 * gamma) + 0.000907 * Math.sin(2 * gamma)
        - 0.002697 * Math.cos(3 * gamma) + 0.00148 * Math.sin(3 * gamma);
    const latitude = CENTRAL_EUROPE_LATITUDE_DEGREES * radians;
    const hourAngle = (time / 720 - 1) * Math.PI;
    const x = -Math.cos(declination) * Math.sin(hourAngle);
    const y = Math.sin(latitude) * Math.sin(declination) + Math.cos(latitude) * Math.cos(declination) * Math.cos(hourAngle);
    const z = Math.sin(latitude) * Math.cos(declination) * Math.cos(hourAngle) - Math.cos(latitude) * Math.sin(declination);
    const elevationRadians = Math.asin(clamp(y, -1, 1));
    // Geometric center at horizon, matching the renderer and automatic darkness.
    const sunsetHourAngle = Math.acos(clamp(-Math.tan(latitude) * Math.tan(declination), -1, 1));
    const halfDayMinutes = sunsetHourAngle / Math.PI * 720;
    return { elevationRadians, elevationDegrees: elevationRadians / radians,
        azimuthRadians: (Math.atan2(x, -z) + TAU) % TAU, sunDirection: { x, y, z }, declinationRadians: declination,
        daylightHours: halfDayMinutes / 30, sunriseMinutes: 720 - halfDayMinutes, sunsetMinutes: 720 + halfDayMinutes };
}
/** One-time real Central-European date -> fixed 365-day gameplay cycle.
 * Leap-day maps to day 59; March and later retain their non-leap ordinal. */
export function centralEuropeanDayOfYear(epoch) {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Prague", month: "numeric", day: "numeric" }).formatToParts(new Date(epoch));
    const month = Number(parts.find(part => part.type === "month").value);
    const day = Number(parts.find(part => part.type === "day").value);
    const monthDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return monthDays.slice(0, month - 1).reduce((sum, value) => sum + value, 0) + Math.min(day, monthDays[month - 1]);
}
/** Deterministic gameplay climate: annual mean 9 C, amplitude 11.5 C, July peak
 * day 205. Smooth daily minimum 30 minutes after sunrise, maximum at 15:00. */
export function climateAirTemperature(dayOfYear, worldTimeMinutes, weather) {
    const time = ((worldTimeMinutes % 1440) + 1440) % 1440;
    const seasonal = 9 + 11.5 * Math.cos(TAU * (dayOfYear - 205 + time / 1440) / CLIMATE_DAYS_PER_YEAR);
    const minimumAt = sampleSolar(dayOfYear, 720).sunriseMinutes + 30;
    const maximumAt = 900;
    const phase = time >= minimumAt && time <= maximumAt
        ? Math.PI + Math.PI * (time - minimumAt) / (maximumAt - minimumAt)
        : Math.PI * ((time - maximumAt + 1440) % 1440) / (1440 - maximumAt + minimumAt);
    const amplitude = 4.5 * (1 - weather.cloudCoverage * 0.72);
    const regimeCooling = weather.rainIntensity * 1.8 + weather.snowfallIntensity * 2.2 + weather.hailIntensity * 1.5;
    return clamp(seasonal + amplitude * Math.cos(phase) - regimeCooling, -35, 40);
}
export const MAX_SNOW_WATER_EQUIVALENT_MM = 250;
export function snowCoverFromWaterEquivalent(swe) { return Math.pow(unit(swe / 12), 0.75); }
export function waterEquivalentFromSnowCover(cover) { return 12 * Math.pow(unit(cover), 1 / 0.75); }
/** Bounded gameplay density approximation, not a universal 10:1 ratio. */
export function snowToWaterRatio(airTemperatureC) { return clamp(8 - airTemperatureC * 0.65, 6, 18); }
export function snowDepthFromWaterEquivalent(swe, airTemperatureC) { return swe * snowToWaterRatio(airTemperatureC) / 10; }
/** Integrates a small world-time step (server uses <=5 game minutes). SWE rates
 * are mm per game hour; temperature relaxation is an exponential in game hours. */
export function advanceSurfaceClimate(surface, weather, airTemperatureC, solarElevationRadians, worldSeconds) {
    if (worldSeconds <= 0)
        return { ...surface };
    const hours = worldSeconds / 3600;
    const solarHeating = Math.max(0, Math.sin(solarElevationRadians)) * (1 - weather.cloudCoverage * 0.75);
    const groundTarget = airTemperatureC + 3.5 * solarHeating * (1 - surface.snowCover * 0.7);
    let groundTemperatureC = surface.groundTemperatureC + (groundTarget - surface.groundTemperatureC) * -Math.expm1(-hours / (4 + surface.snowCover * 4));
    const snowfall = 2.2 * weather.snowfallIntensity * hours;
    // Smooth retention from strong below freezing to none on +2 C ground.
    const sticking = 1 - smooth((groundTemperatureC + 0.5) / 2.5);
    const meltedOnContact = snowfall * (1 - sticking);
    groundTemperatureC -= Math.min(Math.max(0, groundTemperatureC), meltedOnContact * 0.6);
    const meltRate = Math.max(0, airTemperatureC) * 0.16 + Math.max(0, groundTemperatureC) * 0.32
        + solarHeating * 0.35 + weather.rainIntensity * (0.15 + Math.max(0, airTemperatureC) * 0.12);
    const availableSnow = surface.snowWaterEquivalentMm + snowfall * sticking;
    const meltedPack = Math.min(availableSnow, meltRate * hours);
    const snowWaterEquivalentMm = clamp(availableSnow - meltedPack, 0, MAX_SNOW_WATER_EQUIVALENT_MM);
    const waterInput = (weather.rainIntensity * 3.5 + weather.hailIntensity * 1.4) * hours + meltedOnContact + meltedPack;
    const evaporation = (weather.precipitationIntensity < 0.05 ? 0.12 + solarHeating * 0.15 : 0.015) * hours;
    return { groundTemperatureC: clamp(groundTemperatureC, -40, 50), snowWaterEquivalentMm,
        snowDepthCm: snowDepthFromWaterEquivalent(snowWaterEquivalentMm, airTemperatureC), snowCover: snowCoverFromWaterEquivalent(snowWaterEquivalentMm),
        groundWetness: unit(surface.groundWetness + waterInput / 4 - evaporation) };
}
